import React, { Suspense, useEffect } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";
import {
	ZomentumRoutes,
	V2ZomentumRoutes,
	defaultZomentumDocPaperSize
} from "@/Contracts";
import { Layout } from "antd";
import useAccountStatus from "@/Hooks/useAccountStatus";
import { BillingStatusEnum } from "@/Contracts/Users";
import AppLeftMenu from "@organisms/ZLeftMenu";
import useMobileView from "@/Hooks/useMobileView";
import { isDocumentClientView } from "@/Contracts/Utils";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import { isEmbedded } from "@/Utils/superops/embedDetection";

const CopilotSidebar = React.lazy(() => import("@/Copilot/CopilotSidebar"));

interface IDashboardLayout {
	readonly updateServiceWorker: (
		registration: ServiceWorkerRegistration
	) => void;
}

export const DashboardLayout: React.FC<IDashboardLayout> = ({
	children,
	updateServiceWorker
}) => {
	const { appUpdateBannerVisible } = useSelector((state: GlobalState) => ({
		appUpdateBannerVisible: state.settings.appUpdateBannerVisible
	}));
	const hideSidepanel = useAccountStatus(BillingStatusEnum.Cancelled);

	const isDocumentRoute = useRouteMatch(ZomentumRoutes.DocumentsDetail);
	const isVerifyPaymentRoute = useRouteMatch(ZomentumRoutes.VerifyPayment);
	const isTemplateRoute = useRouteMatch(ZomentumRoutes.TemplatesDetail);
	const isDocumentSuccessSignature = useRouteMatch(
		ZomentumRoutes.DocumentSuccessSignature
	);
	const isLoginOrSignup = useRouteMatch(ZomentumRoutes.LoginOrSignup);
	const isLogin = useRouteMatch(ZomentumRoutes.Login);
	const isVerifyEmail = useRouteMatch(ZomentumRoutes.VerifyEmail);
	const isSetupAccount = useRouteMatch(ZomentumRoutes.SetupAccount);
	const isAuthorize = useRouteMatch(ZomentumRoutes.Authorize);
	const isDocumentsPdfView = useRouteMatch(ZomentumRoutes.DocumentsPdfView);
	const isDocumentsPreview = useRouteMatch(ZomentumRoutes.DocumentsPreview);

	const isBillingRoute = useRouteMatch(ZomentumRoutes.Billing);
	const isBillingPageRoute = useRouteMatch(V2ZomentumRoutes.BILLING);

	const location = useLocation();
	const isEmbeddedMode = isEmbedded();
	// const authToken = cache.get(ZomentumStorageKeys.Token, true) as AuthToken;
	const shouldHideLayout = [
		isDocumentRoute?.isExact,
		isTemplateRoute?.isExact,
		isDocumentSuccessSignature?.isExact,
		isLoginOrSignup?.isExact,
		isLogin?.isExact,
		isSetupAccount?.isExact,
		isVerifyEmail?.isExact,
		isAuthorize?.isExact,
		isDocumentsPdfView?.isExact,
		isDocumentsPreview?.isExact,
		isBillingRoute?.isExact,
		isBillingPageRoute?.isExact,
		isVerifyPaymentRoute?.isExact,
		hideSidepanel,
		isEmbeddedMode
	].some(a => !!a);

	const isMobileView = useMobileView();

	const isEndClientView = isDocumentClientView(location.pathname);
	const isPdfView = useRouteMatch(ZomentumRoutes.DocumentsPdfView)?.isExact;
	useEffect(() => {
		const autoRefresh = isEndClientView || isPdfView || isLoginOrSignup;

		if (autoRefresh && appUpdateBannerVisible) {
			// reload when update is available in public page, the in next reload 'appUpdateBannerVisible' will become false
			// as content is updated
			window.location.reload();
		}
	}, [appUpdateBannerVisible]);

	const { currentActiveDocument, currentActiveTemplate } = useSelector(
		(state: GlobalState) => {
			return {
				currentActiveDocument: state.documents.currentActiveDocument,
				currentActiveTemplate: state.templates.currentActiveTemplate
			};
		}
	);
	const isDocumentPage = location.pathname.startsWith("/documents/");
	const isTemplatePage = location.pathname.startsWith("/templates/");
	const isDocumentsViewPdfPage = location.pathname.startsWith(
		"/documents/view/pdf/"
	);
	const getPaperSize = () => {
		if (isDocumentsViewPdfPage) {
			return currentActiveDocument?.paper_size;
		}
		if (isDocumentPage) {
			return currentActiveDocument?.paper_size;
		}
		if (isTemplatePage) {
			return currentActiveTemplate?.paper_size;
		}
		return defaultZomentumDocPaperSize;
	};

	return (
		<Layout
			style={{ flexGrow: 1 }}
			className={`paper-size-${
				getPaperSize() ?? defaultZomentumDocPaperSize
			} ${
				currentActiveDocument?.is_single_block
					? "single-block-editor"
					: ""
			}`}
		>
			{!shouldHideLayout && !isMobileView && (
				<AppLeftMenu updateServiceWorker={updateServiceWorker} />
			)}
			{!shouldHideLayout && !isMobileView && (
				<Suspense fallback={null}>
					<CopilotSidebar />
				</Suspense>
			)}
			<Layout.Content className="outer-container">
				{children}
			</Layout.Content>
		</Layout>
	);
};
